/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import moment from 'moment';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Link, Typography2 } from '@coursera/cds-core';

import type { AiCoachClipRecommendationsFragment } from 'bundles/ai-course-coach/queries/__generated__/aiCoachClipRecommendationsFragment';
import { generateConsumerVideoLink } from 'bundles/ai-course-coach/utils/links';

import _t from 'i18n!nls/learning-assistant-chat-ai';

function Clip({
  course: { slug: courseSlug, name: courseName, id: courseId },
  duration,
  itemId,
  itemSlug,
  itemTitle,
  partners,
  previewImage,
  currentCourseId,
}: AiCoachClipRecommendationsFragment & { currentCourseId?: string }) {
  const track = useRetracked();
  const imgixProps = {
    alt: '',
    imgParams: {
      fit: 'fill',
      auto: 'format,compress',
      quality: '80',
    },
    maxWidth: '90px',
  };

  const partnerLogoImgixProps = {
    imgParams: {
      fit: 'fill',
      auto: 'format,compress',
      quality: '80',
    },
    maxWidth: '20px',
  };

  return (
    <div
      css={css`
        display: flex;
        margin: var(--cds-spacing-50) 0 var(--cds-spacing-200) 0;
      `}
    >
      <div
        css={css`
          width: 90px;
          min-width: 90px;
          max-width: 90px;
          margin: 0 var(--cds-spacing-100);
        `}
      >
        <Imgix
          {...imgixProps}
          src={previewImage}
          style={{ border: `1px solid var(--cds-color-neutral-disabled)`, borderRadius: 'var(--cds-border-radius-50)' }}
        />
      </div>
      <div>
        <div
          css={css`
            display: flex;
          `}
        >
          {partners && partners.length > 0 && (
            <Fragment>
              {/*
               // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
              {partners[0].logo && (
                <div
                  css={css`
                    height: 22px;
                    width: 22px;
                    border: 1px solid var(--cds-color-neutral-disabled);
                    border-radius: var(--cds-border-radius-50);
                    margin: 0 var(--cds-spacing-50) 0 0;
                  `}
                >
                  <Imgix
                    {...partnerLogoImgixProps}
                    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    alt={_t('Logo for #{name}', { name: partners[0].name })}
                    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    src={partners[0].logo}
                    style={{ marginBottom: 'var(--cds-spacing-50)' }}
                  />
                </div>
              )}
              {/*
               // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
              {partners.length > 1 && partners[1].logo && (
                <div
                  css={css`
                    height: 22px;
                    width: 22px;
                    border: 1px solid var(--cds-color-neutral-disabled);
                    border-radius: var(--cds-border-radius-50);
                    margin: 0 var(--cds-spacing-50) 0 0;
                  `}
                >
                  <Imgix
                    {...partnerLogoImgixProps}
                    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    alt={_t('Logo for #{name}', { name: partners[1].name })}
                    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    src={partners[1].logo}
                    style={{ marginBottom: 'var(--cds-spacing-50)' }}
                  />
                </div>
              )}
            </Fragment>
          )}
          <Typography2 component="span" variant="bodySecondary" color="supportText">
            {partners && partners.length > 1
              ? // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                `${partners[0].name}, ${partners[1].name}`
              : // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                partners && partners[0].name}
          </Typography2>
        </div>
        <Typography2
          component="h3"
          variant="subtitleMedium"
          css={css`
            margin: var(--cds-spacing-50) 0 var(--cds-spacing-50) 0;
          `}
        >
          {itemTitle}
        </Typography2>
        <Typography2 component="span" variant="bodySecondary" color="supportText">
          {_t('From the course: #{courseName}', { courseName })}
        </Typography2>
        <Typography2
          component="p"
          variant="bodySecondary"
          color="supportText"
          css={css`
            margin: 0 0 var(--cds-spacing-50) 0;
          `}
        >
          {humanizeLearningTime(moment.duration(duration))}
        </Typography2>
        <Link
          aria-label={_t('Open video page for #{itemTitle}', { itemTitle })}
          href={
            generateConsumerVideoLink({ courseSlug, itemSlug, itemId, courseId, currentCourseId }) + '?backenabled=1'
          }
          variant="quiet"
          typographyVariant="bodySecondary"
          onClick={() => {
            track({
              trackingData: {
                course_slug: courseSlug,
                item_slug: itemSlug,
                item_id: itemId,
                timestamp: Date.now(),
              },
              trackingName: 'ai_course_coach_chat_clip_recommendation_clip_link',
              action: `click`,
            });
          }}
        >
          {_t('Open video page')}
        </Link>
      </div>
    </div>
  );
}

export default Clip;
